import { AxiosInstance } from "axios";
import { QueuedRequest, TokenStorage } from "../types";
import { TokenRefreshService } from "../services/token-refresh-service";

export class ErrorInterceptor {
  private isRefreshing = false;
  private failedQueue: QueuedRequest[] = [];

  constructor(
    private tokenStorage: TokenStorage,
    private tokenRefreshService: TokenRefreshService
  ) {}

  response = async (error: unknown): Promise<unknown> => {
    const originalRequest = (error as Record<string, unknown>).config as Record<
      string,
      unknown
    >;

    if (
      (error as Record<string, unknown>).response &&
      ((error as Record<string, unknown>).response as Record<string, unknown>)
        .status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (this.isRefreshing) {
        return this.queueRequest(originalRequest);
      }

      return this.handleTokenRefresh(originalRequest);
    }

    return Promise.reject(error);
  };

  private async queueRequest(originalRequest: unknown): Promise<unknown> {
    return new Promise<void>((resolve, reject) => {
      this.failedQueue.push({ resolve: resolve as () => void, reject });
    }).then(() => {
      const api = this.findApiForRequest(originalRequest);
      return api(originalRequest as Record<string, unknown>);
    });
  }

  private async handleTokenRefresh(originalRequest: unknown): Promise<unknown> {
    this.isRefreshing = true;

    try {
      const newToken = await this.tokenRefreshService.refreshToken();
      this.tokenStorage.setAccessToken(newToken);

      this.processFailedQueue();

      const api = this.findApiForRequest(originalRequest);
      return api(originalRequest as Record<string, unknown>);
    } catch (refreshError) {
      this.processFailedQueue(refreshError);
      this.tokenStorage.clearTokens();
      return Promise.reject(refreshError);
    } finally {
      this.isRefreshing = false;
    }
  }

  private processFailedQueue(error?: unknown): void {
    this.failedQueue.forEach((request) => {
      if (error) {
        request.reject(error);
      } else {
        request.resolve();
      }
    });

    this.failedQueue = [];
  }

  private findApiForRequest(_config: unknown): AxiosInstance {
    // This needs to be injected or referenced from the main client
    // Use the config in the message to avoid unused var linting
    throw new Error(
      `API instance finder not implemented for config: ${JSON.stringify(
        _config
      )}`
    );
  }

  setApiFinder(finder: (config: unknown) => AxiosInstance): void {
    this.findApiForRequest = finder;
  }
}
